<template>
    <ManagePageContainer>
        <ManageSidebar />
        <MainContentWrapper>
            <!-- * 헤더 섹션 -->
            <HeaderSection>
                <HeaderTitle>
                    <h1>문서 관리</h1>
                    <span>업로드된 문서 파일을 관리합니다</span>
                </HeaderTitle>
            </HeaderSection>

            <!-- * 검색 및 필터 섹션 -->
            <SearchSection>
                <SearchCard>
                    <SearchRow>
                        <SearchGroup>
                            <label>검색 조건</label>
                            <SearchField>
                                <select v-model="searchType">
                                    <option value="fileName">파일명</option>
                                    <option value="fileType">파일유형</option>
                                </select>
                            </SearchField>
                            <SearchInput type="text" v-model="searchTerm" :placeholder="getSearchPlaceholder()"
                                @keyup.enter="searchDocuments" />
                            <SearchButton @click="searchDocuments">
                                <Magnify :size="20" />
                            </SearchButton>
                        </SearchGroup>
                    </SearchRow>

                    <SearchRow>
                        <FilterGroup>
                            <label>파일유형</label>
                            <FilterChips>
                                <FilterChip v-for="type in fileTypes" :key="type.value"
                                    :active="selectedFileType === type.value" @click="filterByFileType(type.value)">
                                    {{ type.label }}
                                </FilterChip>
                            </FilterChips>
                        </FilterGroup>
                    </SearchRow>
                </SearchCard>
            </SearchSection>

            <!-- * 통계 카드 -->
            <!-- <StatsSection>
                <StatsCard>
                    <StatsNumber>{{ totalDocuments }}</StatsNumber>
                    <StatsLabel>전체 문서</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber>{{ imageFiles }}</StatsNumber>
                    <StatsLabel>이미지 파일</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber>{{ pdfFiles }}</StatsNumber>
                    <StatsLabel>PDF 파일</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber>{{ otherFiles }}</StatsNumber>
                    <StatsLabel>기타 파일</StatsLabel>
                </StatsCard>
            </StatsSection> -->

            <!-- * 문서 목록 테이블 -->
            <TablesContainer>
                <!-- * 고객 등록 목록 테이블 -->
                <TableSection>
                    <TableHeader>
                        <SubTitle>고객 등록 목록</SubTitle>
                        <!-- 검색창 -->
                        <SearchGroup>
                            <SearchInput type="text" v-model="beneficiarySearchTerm" placeholder="고객명 검색"
                                @keyup.enter="searchBeneficiaries" />
                            <SearchButton @click="searchBeneficiaries">
                                <Magnify :size="16" />
                            </SearchButton>
                        </SearchGroup>
                        <AddButton @click="openBeneficiaryModal">
                            <Plus :size="20" style="padding-top: 2px;" />
                            고객 등록
                        </AddButton>
                    </TableHeader>

                    <!-- * 로딩 상태 -->
                    <LoadingContainer v-if="isLoading">
                        <LoadingSpinner>고객 목록을 불러오는 중...</LoadingSpinner>
                    </LoadingContainer>

                    <!-- * 테이블 뷰 -->
                    <TableContainer v-else>
                        <ModernTable>
                            <thead>
                                <tr>
                                    <th>고객명</th>
                                    <th>전화번호</th>
                                    <th>문서개수</th>
                                    <th>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="beneficiary in paginatedBeneficiaries" :key="beneficiary.name"
                                    @click="selectBeneficiary(beneficiary)"
                                    :class="{ 'selected': selectedBeneficiary && selectedBeneficiary.name === beneficiary.name, 'blurred': isTestUser }"
                                    style="cursor: pointer;">
                                    <td>
                                        <BeneficiaryName>{{ beneficiary.name }}</BeneficiaryName>
                                    </td>
                                    <td>
                                        <BeneficiaryName>{{ beneficiary.phone }}</BeneficiaryName>
                                    </td>
                                    <td>
                                        <DocumentCountBadge>{{ beneficiary.keyCount }}</DocumentCountBadge>
                                    </td>
                                    <td>
                                        <ActionButtons>
                                            <ActionButton @click.stop="editBeneficiary(beneficiary)" class="edit"
                                                title="수정">✏️</ActionButton>
                                            <ActionButton @click.stop="deleteBeneficiary(beneficiary)" class="delete"
                                                title="삭제">🗑️</ActionButton>
                                        </ActionButtons>
                                    </td>
                                </tr>
                            </tbody>
                        </ModernTable>

                        <!-- * 빈 상태 -->
                        <EmptyState v-if="filteredBeneficiaries.length === 0">
                            <EmptyIcon>👥</EmptyIcon>
                            <EmptyMessage>
                                {{ beneficiarySearchTerm ? '검색 결과가 없습니다' : '등록된 수급자가 없습니다' }}
                            </EmptyMessage>
                            <EmptySubMessage>
                                {{ beneficiarySearchTerm ? '다른 검색어로 시도해보세요' : '상단의 "수급자 등록" 버튼을 클릭하여 수급자를 등록하세요' }}
                            </EmptySubMessage>
                        </EmptyState>
                    </TableContainer>

                    <!-- * 페이지네이션 -->
                    <PaginationContainer v-if="beneficiaries.length > 0">
                        <Pagination>
                            <PaginationButton :disabled="beneficiaryCurrentPage === 1" @click="goToBeneficiaryPage(1)">
                                <ChevronDoubleLeft :size="20" />
                            </PaginationButton>
                            <PaginationButton :disabled="beneficiaryCurrentPage <= 10"
                                @click="goToBeneficiaryPageGroup('prev')">
                                <ChevronLeft :size="20" />
                            </PaginationButton>

                            <PaginationNumber v-for="page in beneficiaryDisplayedPages" :key="page"
                                :active="page === beneficiaryCurrentPage" @click="goToBeneficiaryPage(page)">
                                {{ page }}
                            </PaginationNumber>

                            <PaginationButton
                                :disabled="beneficiaryCurrentPage > Math.floor(beneficiaryTotalPages / 10) * 10"
                                @click="goToBeneficiaryPageGroup('next')">
                                <ChevronRight :size="20" />
                            </PaginationButton>
                            <PaginationButton :disabled="beneficiaryCurrentPage === beneficiaryTotalPages"
                                @click="goToBeneficiaryPage(beneficiaryTotalPages)">
                                <ChevronDoubleRight :size="20" />
                            </PaginationButton>
                        </Pagination>
                    </PaginationContainer>
                </TableSection>

                <!-- * 문서 목록 테이블 -->
                <TableSection>
                    <TableHeader>
                        <SubTitle>
                            {{ selectedBeneficiary ? `${isTestUser ? '테스트' : selectedBeneficiary.name}님의 문서 목록` : '문서목록'
                            }}
                        </SubTitle>
                        <ButtonGroup v-if="selectedBeneficiary">
                            <AddButton @click="openSmsSendModal">
                                <Send :size="20" style="padding-top: 2px;" />
                                SMS 발송
                            </AddButton>
                            <AddButton @click="openUploadModal">
                                <Plus :size="20" style="padding-top: 2px;" />
                                문서 업로드
                            </AddButton>
                        </ButtonGroup>
                    </TableHeader>

                    <!-- * 선택된 수급자가 없을 때 -->
                    <EmptyState v-if="!selectedBeneficiary">
                        <EmptyIcon>👈</EmptyIcon>
                        <EmptyMessage>수급자를 선택해주세요</EmptyMessage>
                        <EmptySubMessage>좌측 수급자 목록에서 수급자를 클릭하면 해당 수급자의 문서를 확인할 수 있습니다.</EmptySubMessage>
                    </EmptyState>

                    <!-- * 로딩 상태 -->
                    <LoadingContainer v-else-if="isLoading">
                        <LoadingSpinner>문서 목록을 불러오는 중...</LoadingSpinner>
                    </LoadingContainer>

                    <!-- * 테이블 뷰 -->
                    <TableContainer v-else>
                        <ModernTable>
                            <thead>
                                <tr>
                                    <th>파일명</th>
                                    <th>파일유형</th>
                                    <th>파일크기</th>
                                    <th>등록일</th>
                                    <th>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="document in paginatedDocuments" :key="document.key"
                                    @click="previewDocument(document)">
                                    <td>
                                        <DocumentInfo>
                                            <FileIcon>{{ getFileIcon(document.fileName) }}</FileIcon>
                                            <InfoText>
                                                <FileName>{{ document.fileName }}</FileName>
                                            </InfoText>
                                        </DocumentInfo>
                                    </td>
                                    <td>
                                        <FileTypeChip :type="getFileExtension(document.fileName)">
                                            {{ getFileExtension(document.fileName).toUpperCase() }}
                                        </FileTypeChip>
                                    </td>
                                    <td>
                                        <FileSize>{{ formatFileSize(document.size) }}</FileSize>
                                    </td>
                                    <td>{{ formatDate(document.lastModified) }}</td>
                                    <td>
                                        <ActionButtons>
                                            <ActionButton @click.stop="downloadDocument(document)" class="download">
                                                ⬇️
                                            </ActionButton>
                                            <ActionButton @click.stop="deleteDocument(document.key)" class="delete">
                                                🗑️
                                            </ActionButton>
                                        </ActionButtons>
                                    </td>
                                </tr>
                            </tbody>
                        </ModernTable>

                        <!-- * 빈 상태 -->
                        <EmptyState v-if="filteredDocuments.length === 0">
                            <EmptyIcon>📄</EmptyIcon>
                            <EmptyMessage>{{ selectedBeneficiary.name }}님의 문서가 없습니다</EmptyMessage>
                            <EmptySubMessage>우측 상단의 '문서 업로드' 버튼을 클릭하여 파일을 업로드하세요</EmptySubMessage>
                        </EmptyState>
                    </TableContainer>

                    <!-- * 페이지네이션 -->
                    <PaginationContainer v-if="selectedBeneficiary && filteredDocuments.length > 0">
                        <Pagination>
                            <PaginationButton :disabled="currentPage === 1" @click="goToPage(1)">
                                <ChevronDoubleLeft :size="20" />
                            </PaginationButton>
                            <PaginationButton :disabled="currentPage <= 10" @click="goToPageGroup('prev')">
                                <ChevronLeft :size="20" />
                            </PaginationButton>

                            <PaginationNumber v-for="page in displayedPages" :key="page" :active="page === currentPage"
                                @click="goToPage(page)">
                                {{ page }}
                            </PaginationNumber>

                            <PaginationButton :disabled="currentPage > Math.floor(totalPages / 10) * 10"
                                @click="goToPageGroup('next')">
                                <ChevronRight :size="20" />
                            </PaginationButton>
                            <PaginationButton :disabled="currentPage === totalPages" @click="goToPage(totalPages)">
                                <ChevronDoubleRight :size="20" />
                            </PaginationButton>
                        </Pagination>
                    </PaginationContainer>
                </TableSection>
            </TablesContainer>
        </MainContentWrapper>

        <!-- * SMS 발송 모달 -->
        <SmsSendModal v-if="showSmsSendModal" :recipient="selectedBeneficiary" :documents="documentsForSms"
            @close="showSmsSendModal = false" @send="handleSmsSend" />

        <!-- * 문서 업로드 모달 -->
        <UploadDocumentModal v-if="showUploadModal" :selected-beneficiary="selectedBeneficiary"
            @close="closeUploadModal" @upload-success="handleUploadSuccess" />

        <!-- * 수급자 등록 모달 -->
        <BeneficiaryModal v-if="showBeneficiaryModal" @close="closeBeneficiaryModal" @save="saveBeneficiary" />

        <!-- * 문서 미리보기 모달 -->
        <DocumentPreviewModal v-if="showPreviewModal" :document="selectedDocument"
            :selected-beneficiary="selectedBeneficiary" @close="closePreviewModal" />

    </ManagePageContainer>
</template>

<script>
import ManageSidebar from './ManageSidebar.vue';
import * as ManageDocumentCss from '@/assets/styles/manage/ManageDocumentCss.js';
import BeneficiaryModal from './modal/BeneficiaryModal.vue';
import UploadDocumentModal from './modal/UploadDocumentModal.vue';
import DocumentPreviewModal from './modal/DocumentPreviewModal.vue';
import SmsSendModal from './modal/SmsSendModal.vue';

import ChevronDoubleLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import ChevronDoubleRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import Magnify from 'vue-material-design-icons/Magnify.vue';
import Plus from 'vue-material-design-icons/Plus.vue';
import Send from 'vue-material-design-icons/Send.vue';

export default {
    name: 'ManageDocument',

    components: {
        ManageSidebar,
        ...ManageDocumentCss,
        BeneficiaryModal,
        UploadDocumentModal,
        DocumentPreviewModal,
        SmsSendModal,
        ChevronDoubleLeft,
        ChevronDoubleRight,
        ChevronLeft,
        ChevronRight,
        Magnify,
        Plus,
        Send
    },

    data() {
        return {
            documents: [], // 문서 데이터
            beneficiaries: [], // 수급자 데이터
            documentCounts: {}, // 각 수급자별 문서 개수를 저장

            // 로딩 상태
            isLoading: false,
            isUploading: false,

            // 검색 관련
            searchType: 'fileName',
            searchTerm: '',
            selectedFileType: 'all',
            beneficiarySearchTerm: '', // 수급자 검색 키워드

            // 페이징
            currentPage: 1,
            itemsPerPage: 10,
            beneficiaryCurrentPage: 1,
            beneficiaryItemsPerPage: 5,
            selectedBeneficiary: null, // 선택된 수급자

            // 모달 상태
            showUploadModal: false,
            showBeneficiaryModal: false,
            showPreviewModal: false,
            selectedFiles: [],
            isDragOver: false,
            uploadProgress: 0,

            // 필터 옵션
            fileTypes: [
                { value: 'all', label: '전체' },
                { value: 'image', label: '이미지' },
                { value: 'pdf', label: 'PDF' },
                { value: 'document', label: '문서' },
                { value: 'other', label: '기타' }
            ],

            // SMS 발송 관련
            showSmsSendModal: false,
            documentsForSms: [],

            // 문서 미리보기 관련
            selectedDocument: null,
        }
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        // 수급자 검색 필터링
        filteredBeneficiaries() {
            if (!this.beneficiarySearchTerm) {
                return this.beneficiaries;
            }

            return this.beneficiaries.filter(beneficiary =>
                beneficiary.name.toLowerCase().includes(this.beneficiarySearchTerm.toLowerCase()) ||
                (beneficiary.number && beneficiary.number.toString().includes(this.beneficiarySearchTerm))
            );
        },

        // 수급자 페이지네이션
        paginatedBeneficiaries() {
            const start = (this.beneficiaryCurrentPage - 1) * this.beneficiaryItemsPerPage;
            const end = start + this.beneficiaryItemsPerPage;

            return this.filteredBeneficiaries.slice(start, end).map(beneficiary => ({
                ...beneficiary,
                keyCount: this.documentCounts[beneficiary.name] || 0 // loadDocuments에서 가져온 total 사용
            }));
        },

        beneficiaryTotalPages() {
            return Math.ceil(this.filteredBeneficiaries.length / this.beneficiaryItemsPerPage);
        },

        beneficiaryDisplayedPages() {
            const pages = [];
            const maxDisplayed = 5;
            let start = Math.max(1, this.beneficiaryCurrentPage - Math.floor(maxDisplayed / 2));
            let end = Math.min(this.beneficiaryTotalPages, start + maxDisplayed - 1);

            if (end - start + 1 < maxDisplayed) {
                start = Math.max(1, end - maxDisplayed + 1);
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        },

        // 선택된 수급자의 문서만 필터링
        selectedBeneficiaryDocuments() {
            return this.documents;
        },

        filteredDocuments() {
            let filtered = this.selectedBeneficiaryDocuments;

            // 검색 필터
            if (this.searchTerm) {
                filtered = filtered.filter(document => {
                    if (this.searchType === 'fileName') {
                        return document.fileName.toLowerCase().includes(this.searchTerm.toLowerCase());
                    }
                    return true;
                });
            }

            // 파일 타입 필터
            if (this.selectedFileType !== 'all') {
                filtered = filtered.filter(document => {
                    const extension = this.getFileExtension(document.fileName);
                    switch (this.selectedFileType) {
                        case 'image':
                            return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
                        case 'pdf':
                            return extension === 'pdf';
                        case 'document':
                            return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension);
                        default:
                            return true;
                    }
                });
            }

            return filtered;
        },

        paginatedDocuments() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredDocuments.slice(start, end);
        },

        totalPages() {
            return Math.ceil(this.filteredDocuments.length / this.itemsPerPage);
        },

        displayedPages() {
            const pages = [];
            const maxDisplayed = 5;
            let start = Math.max(1, this.currentPage - Math.floor(maxDisplayed / 2));
            let end = Math.min(this.totalPages, start + maxDisplayed - 1);

            if (end - start + 1 < maxDisplayed) {
                start = Math.max(1, end - maxDisplayed + 1);
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        },

        // 통계 계산
        totalDocuments() {
            return this.documents.length;
        },

        imageFiles() {
            return this.documents.filter(doc => {
                const ext = this.getFileExtension(doc.fileName);
                return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
            }).length;
        },

        pdfFiles() {
            return this.documents.filter(doc =>
                this.getFileExtension(doc.fileName) === 'pdf'
            ).length;
        },

        otherFiles() {
            return this.totalDocuments - this.imageFiles - this.pdfFiles;
        }
    },

    async mounted() {
        await this.loadBeneficiaries();
        await this.loadAllDocumentCounts();
    },

    methods: {
        openSmsSendModal() {
            // 현재 선택된 수급자의 문서 목록을 documentsForSms에 매핑
            this.documentsForSms = this.documents.map(doc => ({
                id: doc.key,  // 문서의 고유 키를 id로 사용
                name: doc.fileName,
                size: doc.size,
                createdAt: doc.lastModified
            }));
            this.showSmsSendModal = true;
        },

        async handleSmsSend(sendData) {
            try {
                console.log('발송 데이터:', sendData);
                // 여기에 실제 발송 로직 구현

                this.$swal.fire({
                    title: '발송 완료',
                    text: `${sendData.method === 'kakao' ? '카카오톡' : '문자'}으로 성공적으로 발송되었습니다.`,
                    icon: 'success'
                });

                this.showSmsSendModal = false;
            } catch (error) {
                this.$swal.fire({
                    title: '발송 실패',
                    text: '메시지 발송 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        // ============= 문서 미리보기 관련 =============

        previewDocument(documentData) {
            this.selectedDocument = documentData;
            this.showPreviewModal = true;
            window.document.body.style.overflow = 'hidden';
        },

        closePreviewModal() {
            this.showPreviewModal = false;
            this.selectedDocument = null;
            window.document.body.style.overflow = 'auto';
        },

        // ============= 수급자 관리 =============

        // 수급자 목록 불러오기
        async loadBeneficiaries() {
            this.isLoading = true;
            try {
                const response = await this.$axios({
                    method: 'get',
                    url: `${this.$apiUrls.hanqmedi}/getDocumentBeneficiaries`,
                    params: {
                        search: this.beneficiarySearchTerm || null,
                        companykey: this.$globalState.company_key
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.data && response.data.success) {
                    const beneficiariesData = response.data.data?.beneficiaries || [];

                    this.beneficiaries = beneficiariesData.map(b => ({
                        name: b.name,
                        phone: b.phone,
                        companykey: b.companykey
                    }));

                } else {
                    throw new Error(response.data?.message || '목록을 불러올 수 없습니다.');
                }
            } catch (error) {
                console.error('수급자 목록 조회 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '수급자 목록을 불러오는데 실패했습니다.',
                    icon: 'error'
                });
                this.beneficiaries = [];
            } finally {
                this.isLoading = false;
            }
        },

        // 모든 수급자의 문서 개수 로드
        async loadAllDocumentCounts() {
            for (const beneficiary of this.beneficiaries) {
                await this.loadDocumentCountForBeneficiary(beneficiary.name);
            }
        },

        // 특정 수급자의 문서 개수만 로드
        async loadDocumentCountForBeneficiary(beneficiaryName) {
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/documents`, {
                    params: {
                        beneficiaryName: beneficiaryName,
                        countOnly: true
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.data.success) {
                    // 직접 할당 (Vue 3에서는 반응형)
                    this.documentCounts[beneficiaryName] = response.data.data.total || 0;
                }
            } catch (error) {
                console.error(`${beneficiaryName}의 문서 개수 조회 실패:`, error);
                this.documentCounts[beneficiaryName] = 0;
            }
        },

        // 수급자 검색
        async searchBeneficiaries() {
            this.beneficiaryCurrentPage = 1;
            await this.loadBeneficiaries();
        },

        // 수급자 수정
        editBeneficiary(beneficiary) {
            if (beneficiary.type !== '비급여') {
                this.$swal.fire({
                    title: '수정 불가',
                    text: '해당 수급자는 수정할 수 없습니다. 비급여 수급자만 수정 가능합니다.',
                    icon: 'info',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.$swal.fire({
                title: '수급자 수정',
                html: `
                    <input id="editName" class="swal2-input" placeholder="수급자명" value="${beneficiary.name}">
                    <input id="editPhone" class="swal2-input" placeholder="수급자 전화번호" value="${beneficiary.phone}">
                `,
                showCancelButton: true,
                confirmButtonText: '수정',
                cancelButtonText: '취소',
                preConfirm: () => {
                    const name = document.getElementById('editName').value;
                    const phone = document.getElementById('editPhone').value;

                    if (!name.trim()) {
                        this.$swal.showValidationMessage('수급자명을 입력해주세요');
                        return false;
                    }

                    return {
                        name: name.trim(),
                        phone: phone.trim()  // 전화번호도 함께 반환
                    };
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await this.updateBeneficiary(beneficiary.name, beneficiary.phone, result.value);
                }
            });
        },

        // 수급자 정보 업데이트
        async updateBeneficiary(originalName, originalPhone, updateData) {
            try {
                const response = await this.$axios({
                    method: 'put',
                    url: `${this.$apiUrls.hanqmedi}/updateDocumentBeneficiary/${encodeURIComponent(originalName)}/${encodeURIComponent(originalPhone)}`,
                    data: {
                        beneficiary_name: updateData.name,
                        beneficiary_phone: updateData.phone,
                        companykey: this.$globalState.company_key
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data && response.data.success) {
                    this.$swal.fire({
                        title: '성공',
                        text: '수급자 정보가 수정되었습니다.',
                        icon: 'success'
                    });

                    // 선택된 수급자가 수정된 경우 선택 상태 업데이트
                    if (this.selectedBeneficiary && this.selectedBeneficiary.name === originalName) {
                        this.selectedBeneficiary.name = updateData.name;
                        this.selectedBeneficiary.phone = updateData.phone;
                    }

                    await this.loadBeneficiaries();
                } else {
                    throw new Error(response.data?.message || '수정에 실패했습니다.');
                }
            } catch (error) {
                console.error('수급자 수정 실패:', error);
                this.$swal.fire({
                    title: '수정 실패',
                    text: error.response?.data?.message || '수급자 정보 수정 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        // 수급자 삭제
        async deleteBeneficiary(beneficiary) {
            try {
                const result = await this.$swal.fire({
                    title: '삭제 확인',
                    text: `${beneficiary.name}님을 수급자 목록에서 삭제하시겠습니까?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#d33'
                });

                if (result.isConfirmed) {
                    const response = await this.$axios({
                        method: 'delete',
                        url: `${this.$apiUrls.hanqmedi}/removeDocumentBeneficiary/${encodeURIComponent(beneficiary.name)}/${encodeURIComponent(beneficiary.phone)}`,
                        params: {
                            companykey: this.$globalState.company_key
                        },
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });

                    if (response.data && response.data.success) {
                        this.$swal.fire({
                            title: '성공',
                            text: '수급자가 목록에서 삭제되었습니다.',
                            icon: 'success'
                        });

                        // 삭제된 수급자의 문서 개수 정보 제거 (delete 연산자 사용)
                        delete this.documentCounts[beneficiary.name];

                        if (this.selectedBeneficiary && this.selectedBeneficiary.name === beneficiary.name) {
                            this.selectedBeneficiary = null;
                            this.documents = [];
                        }

                        await this.loadBeneficiaries();
                    } else {
                        throw new Error(response.data?.message || '삭제에 실패했습니다.');
                    }
                }
            } catch (error) {
                console.error('수급자 삭제 실패:', error);
                this.$swal.fire({
                    title: '삭제 실패',
                    text: error.response?.data?.message || '수급자 삭제 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        // ============= 수급자 선택 관련 메서드 =============

        // 개별 수급자 선택/해제
        toggleBeneficiarySelection(beneficiaryName) {
            const index = this.selectedBeneficiaryNames.indexOf(beneficiaryName);
            if (index > -1) {
                this.selectedBeneficiaryNames.splice(index, 1);
            } else {
                this.selectedBeneficiaryNames.push(beneficiaryName);
            }
        },

        // 현재 페이지 전체 선택/해제
        toggleAllCurrentPageSelection() {
            if (this.isAllCurrentPageSelected) {
                this.paginatedBeneficiaries.forEach(beneficiary => {
                    const index = this.selectedBeneficiaryNames.indexOf(beneficiary.name);
                    if (index > -1) {
                        this.selectedBeneficiaryNames.splice(index, 1);
                    }
                });
            } else {
                this.paginatedBeneficiaries.forEach(beneficiary => {
                    if (!this.selectedBeneficiaryNames.includes(beneficiary.name)) {
                        this.selectedBeneficiaryNames.push(beneficiary.name);
                    }
                });
            }
        },

        // 모든 선택 해제
        clearAllSelections() {
            this.selectedBeneficiaryNames = [];
        },

        // 선택된 수급자들 일괄 삭제
        async deleteSelectedBeneficiaries() {
            if (this.selectedBeneficiaryNames.length === 0) {
                this.$swal.fire('알림', '삭제할 수급자를 선택해주세요.', 'warning');
                return;
            }

            try {
                const result = await this.$swal.fire({
                    title: '일괄 삭제 확인',
                    text: `선택된 ${this.selectedBeneficiaryNames.length}명의 수급자를 삭제하시겠습니까?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#d33'
                });

                if (result.isConfirmed) {
                    const deletePromises = this.selectedBeneficiaryNames.map(id =>
                        this.$axios({
                            method: 'delete',
                            url: `${this.$apiUrls.hanqmedi}/removeDocumentBeneficiary/${id}`,
                            params: {
                                companykey: this.$globalState.company_key
                            },
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        })
                    );

                    await Promise.all(deletePromises);

                    this.$swal.fire({
                        title: '성공',
                        text: `${this.selectedBeneficiaryNames.length}명의 수급자가 삭제되었습니다.`,
                        icon: 'success'
                    });

                    this.clearAllSelections();
                    await this.loadBeneficiaries();
                }
            } catch (error) {
                console.error('일괄 삭제 실패:', error);
                this.$swal.fire({
                    title: '삭제 실패',
                    text: '일부 수급자 삭제에 실패했습니다.',
                    icon: 'error'
                });
            }
        },

        // ============= 업로드 모달 관련 =============
        openUploadModal() {
            this.showUploadModal = true;
        },

        closeUploadModal() {
            this.showUploadModal = false;
        },

        async handleUploadSuccess() {
            // 업로드 성공 후 문서 목록 새로고침
            await this.loadDocuments();
            this.closeUploadModal();
        },

        // ============= 수급자 등록 모달 관련 =============
        openBeneficiaryModal() {
            this.showBeneficiaryModal = true;
            document.body.style.overflow = 'hidden';
        },

        closeBeneficiaryModal() {
            this.showBeneficiaryModal = false;
            document.body.style.overflow = 'auto';
        },

        async saveBeneficiary(savedBeneficiaryData) {
            try {
                await this.loadBeneficiaries();

                // 직접 할당으로 새 수급자 문서 개수 초기화
                this.documentCounts[savedBeneficiaryData.name] = 0;

                const registeredBeneficiary = this.beneficiaries.find(b =>
                    b.name === savedBeneficiaryData.name
                );

                if (registeredBeneficiary) {
                    await this.selectBeneficiary(registeredBeneficiary);
                }

                this.closeBeneficiaryModal();
            } catch (error) {
                console.error('수급자 등록 후 처리 실패:', error);
                this.closeBeneficiaryModal();
            }
        },

        // 수급자 선택
        selectBeneficiary(beneficiary) {
            this.selectedBeneficiary = beneficiary;
            this.currentPage = 1; // 문서 목록 페이지 초기화
            this.searchTerm = ''; // 검색어 초기화
            this.selectedFileType = 'all'; // 파일타입 필터 초기화

            // 선택된 수급자의 문서 목록 로드
            this.loadDocuments();
        },

        // 수급자 페이지네이션
        goToBeneficiaryPage(page) {
            if (page >= 1 && page <= this.beneficiaryTotalPages) {
                this.beneficiaryCurrentPage = page;
            }
        },

        goToBeneficiaryPageGroup(direction) {
            if (direction === 'prev') {
                this.beneficiaryCurrentPage = Math.max(1, this.beneficiaryCurrentPage - 10);
            } else {
                this.beneficiaryCurrentPage = Math.min(this.beneficiaryTotalPages, this.beneficiaryCurrentPage + 10);
            }
        },

        changeBeneficiaryItemsPerPage() {
            this.beneficiaryCurrentPage = 1;
        },

        // 문서 목록 조회
        async loadDocuments() {
            if (!this.selectedBeneficiary) {
                this.documents = [];
                return;
            }

            this.isLoading = true;
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/documents`, {
                    params: {
                        search: this.searchTerm || null,
                        type: this.selectedFileType !== 'all' ? this.selectedFileType : null,
                        beneficiaryName: this.selectedBeneficiary.name
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.data.success) {
                    this.documents = response.data.data.documents;

                    // 직접 할당으로 문서 개수 업데이트
                    const totalDocuments = response.data.data.total || this.documents.length;
                    this.documentCounts[this.selectedBeneficiary.name] = totalDocuments;

                } else {
                    throw new Error(response.data.message);
                }
            } catch (error) {
                console.error('문서 목록 조회 실패:', error);
                this.documents = [];
            } finally {
                this.isLoading = false;
            }
        },

        // 문서 업로드
        async uploadFiles() {
            if (!this.selectedFiles.length) return;

            this.isUploading = true;
            this.uploadProgress = 0;

            try {
                const uploadPromises = this.selectedFiles.map(async (file, index) => {
                    const formData = new FormData();
                    formData.append('file', file);

                    // 메타데이터 추가 (필요한 경우)
                    const metadata = {
                        originalName: file.name,
                        uploadDate: new Date().toISOString()
                    };
                    formData.append('metadata', JSON.stringify(metadata));

                    const response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/documents/upload`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            },
                            onUploadProgress: (progressEvent) => {
                                const progress = Math.round(
                                    ((index + progressEvent.loaded / progressEvent.total) / this.selectedFiles.length) * 100
                                );
                                this.uploadProgress = progress;
                            }
                        }
                    );

                    if (!response.data.success) {
                        throw new Error(`${file.name}: ${response.data.message}`);
                    }

                    return response.data;
                });

                await Promise.all(uploadPromises);

                this.$swal.fire({
                    title: '성공',
                    text: `${this.selectedFiles.length}개 파일이 업로드되었습니다.`,
                    icon: 'success'
                });

                this.closeUploadModal();
                await this.loadDocuments(); // 목록 새로고침

            } catch (error) {
                console.error('파일 업로드 실패:', error);
                this.$swal.fire({
                    title: '업로드 실패',
                    text: error.message || '파일 업로드 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            } finally {
                this.isUploading = false;
                this.uploadProgress = 0;
            }
        },

        // 문서 다운로드 수정
        async downloadDocument(documentData) {
            try {

                // ✅ 방법 1: S3 직접 다운로드 시도 (가장 안전한 방법)
                if (documentData.url && documentData.url.includes('s3.ap-northeast-2.amazonaws.com')) {
                    try {
                        await this.downloadFromS3Direct(documentData.url, documentData.fileName);
                        return;
                    } catch (s3Error) {
                        console.warn('S3 직접 다운로드 실패, 서버 경유 시도:', s3Error);
                    }
                }

                // ✅ 방법 2: 기존 바이너리 처리 방식 (개선된 버전)
                const downloadUrl = `${this.$apiUrls.hanqmedi}/documents/${encodeURIComponent(documentData.key)}`;

                // 웹 환경에서는 blob, 일렉트론에서는 arraybuffer 시도
                const responseType = (this.$env && this.$env.isElectron) ? 'arraybuffer' : 'blob';

                const response = await this.$axios.get(downloadUrl, {
                    params: {
                        beneficiaryName: this.selectedBeneficiary.name
                    },
                    responseType: responseType,
                    timeout: 30000
                });

                let blob;

                if (response.data instanceof ArrayBuffer) {
                    // ArrayBuffer인 경우 (일렉트론에서 성공한 경우)
                    const mimeType = this.getMimeTypeFromFileName(documentData.fileName);
                    blob = new Blob([response.data], { type: mimeType });
                    console.log('ArrayBuffer -> Blob 변환 완료:', { originalSize: response.data.byteLength, blobSize: blob.size });
                } else if (response.data instanceof Blob) {
                    // 정상적인 Blob인 경우
                    blob = response.data;
                } else {
                    // 이 경우 더 이상 바이너리 문자열 변환을 시도하지 않고 직접 링크로 넘어감
                    throw new Error('서버 응답 형태가 예상과 다릅니다. 직접 다운로드를 시도합니다.');
                }

                // Blob 크기 검증
                const expectedSize = documentData.size;
                const actualSize = blob.size;

                if (expectedSize && Math.abs(actualSize - expectedSize) > expectedSize * 0.1) {
                    // 크기 차이가 10% 이상이면 손상된 것으로 판단
                    console.warn(`파일 크기 불일치: 예상 ${expectedSize}, 실제 ${actualSize}`);
                    throw new Error('다운로드된 파일 크기가 예상과 다릅니다. 직접 다운로드를 시도합니다.');
                }

                this.downloadBlob(blob, documentData.fileName);

            } catch (error) {
                console.error('파일 다운로드 실패:', error);

                // ✅ 최종 폴백: 새 창에서 직접 다운로드
                try {
                    let finalUrl;
                    if (documentData.url && documentData.url.includes('s3.ap-northeast-2.amazonaws.com')) {
                        // S3 직접 URL이 있으면 사용
                        finalUrl = documentData.url;
                    } else {
                        // 서버 경유 URL
                        finalUrl = `${this.$apiUrls.hanqmedi}/documents/${encodeURIComponent(documentData.key)}?beneficiaryName=${encodeURIComponent(this.selectedBeneficiary.name)}&download=true`;
                    }

                    // 새 창에서 열기
                    const newWindow = window.open(finalUrl, '_blank');

                    if (!newWindow) {
                        // 팝업이 차단된 경우
                        this.$swal.fire({
                            title: '팝업 차단됨',
                            html: `
                                <p>브라우저에서 팝업이 차단되었습니다.</p>
                                <p>아래 링크를 클릭하여 파일을 다운로드하세요:</p>
                                <br>
                                <a href="${finalUrl}" target="_blank" style="color: #007bff; text-decoration: underline; font-weight: bold;">
                                    📁 ${documentData.fileName} 다운로드
                                </a>
                            `,
                            icon: 'info',
                            confirmButtonText: '확인',
                            width: '500px'
                        });
                    } else {
                        this.$swal.fire({
                            title: '다운로드 시작',
                            text: '새 창에서 파일 다운로드가 시작됩니다.',
                            icon: 'success',
                            timer: 2000,
                            showConfirmButton: false
                        });
                    }

                } catch (linkError) {
                    console.error('최종 폴백도 실패:', linkError);
                    this.showDownloadError(error.message);
                }
            }
        },

        // ✅ S3 직접 다운로드 메서드
        async downloadFromS3Direct(s3Url, fileName) {
            try {
                // fetch로 S3에서 직접 다운로드
                const response = await fetch(s3Url, {
                    method: 'GET',
                    mode: 'cors'
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const blob = await response.blob();

                this.downloadBlob(blob, fileName);

            } catch (fetchError) {
                console.error('S3 fetch 실패, 링크 다운로드로 전환:', fetchError);

                // fetch 실패 시 직접 링크로 다운로드
                const link = document.createElement('a');
                link.href = s3Url;
                link.setAttribute('download', fileName);
                link.setAttribute('target', '_blank');
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.$swal.fire({
                    title: '다운로드 시작',
                    text: '파일 다운로드가 시작됩니다.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        },

        // ✅ Blob 다운로드 메서드
        downloadBlob(blob, fileName) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', fileName);
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // URL 정리
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);

            this.$swal.fire({
                title: '다운로드 완료',
                text: `${fileName} 파일이 다운로드되었습니다.`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        },

        // ✅ 파일명에서 MIME 타입 추론하는 메서드
        getMimeTypeFromFileName(fileName) {
            const extension = fileName.split('.').pop().toLowerCase();
            const mimeTypes = {
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'png': 'image/png',
                'gif': 'image/gif',
                'bmp': 'image/bmp',
                'webp': 'image/webp',
                'pdf': 'application/pdf',
                'doc': 'application/msword',
                'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'xls': 'application/vnd.ms-excel',
                'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'ppt': 'application/vnd.ms-powerpoint',
                'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'txt': 'text/plain',
                'csv': 'text/csv',
                'zip': 'application/zip',
                'rar': 'application/x-rar-compressed',
                '7z': 'application/x-7z-compressed'
            };

            return mimeTypes[extension] || 'application/octet-stream';
        },

        // ✅ 다운로드 에러 표시 메서드
        showDownloadError(errorMessage = '알 수 없는 오류가 발생했습니다.') {
            this.$swal.fire({
                title: '다운로드 실패',
                html: `
                    <p>파일 다운로드 중 오류가 발생했습니다.</p>
                    <p style="font-size: 12px; color: #999; margin-top: 10px;">${errorMessage}</p>
                    <br>
                    <p style="font-size: 14px; color: #666;">
                        잠시 후 다시 시도하거나, 관리자에게 문의하세요.
                    </p>
                `,
                icon: 'error',
                confirmButtonText: '확인',
                confirmButtonColor: '#d33'
            });
        },

        // 문서 삭제
        async deleteDocument(key) {
            try {
                const result = await this.$swal.fire({
                    title: '삭제 확인',
                    text: '이 문서를 삭제하시겠습니까?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소'
                });

                if (result.isConfirmed) {
                    const response = await this.$axios.delete(
                        `${this.$apiUrls.hanqmedi}/documents/${encodeURIComponent(key)}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        }
                    );

                    if (response.data.success) {
                        this.$swal.fire({
                            title: '성공',
                            text: '문서가 삭제되었습니다.',
                            icon: 'success'
                        });
                        await this.loadDocuments(); // 목록 새로고침

                        // 선택된 수급자의 문서 개수 업데이트
                        if (this.selectedBeneficiary) {
                            await this.loadDocumentCountForBeneficiary(this.selectedBeneficiary.name);
                        }
                    } else {
                        throw new Error(response.data.message);
                    }
                }
            } catch (error) {
                console.error('문서 삭제 실패:', error);
                this.$swal.fire({
                    title: '삭제 실패',
                    text: error.message || '문서 삭제 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        handleFileSelect(event) {
            const files = Array.from(event.target.files);
            this.addFiles(files);
        },

        handleDragEnter(e) {
            e.preventDefault();
            this.isDragOver = true;
        },

        handleDragOver(e) {
            e.preventDefault();
        },

        handleDragLeave(e) {
            e.preventDefault();
            this.isDragOver = false;
        },

        handleDrop(e) {
            e.preventDefault();
            this.isDragOver = false;
            const files = Array.from(e.dataTransfer.files);
            this.addFiles(files);
        },

        addFiles(files) {
            const validFiles = files.filter(file => {
                // 파일 크기 검증 (10MB)
                if (file.size > 10 * 1024 * 1024) {
                    this.$swal.fire({
                        title: '파일 크기 초과',
                        text: `${file.name}은(는) 10MB를 초과합니다.`,
                        icon: 'warning'
                    });
                    return false;
                }
                return true;
            });

            this.selectedFiles.push(...validFiles);
        },

        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },

        // ============= 검색 및 필터링 =============

        getSearchPlaceholder() {
            const placeholders = {
                fileName: '파일명을 입력하세요',
                fileType: '파일 유형을 입력하세요'
            };
            return placeholders[this.searchType];
        },

        async searchDocuments() {
            this.currentPage = 1;
            await this.loadDocuments();
        },

        async filterByFileType(type) {
            this.selectedFileType = type;
            this.currentPage = 1;
            await this.loadDocuments();
        },

        // ============= 페이지네이션 =============

        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
            }
        },

        goToPageGroup(direction) {
            if (direction === 'prev') {
                this.currentPage = Math.max(1, this.currentPage - 10);
            } else {
                this.currentPage = Math.min(this.totalPages, this.currentPage + 10);
            }
        },

        changeItemsPerPage() {
            this.currentPage = 1;
        },

        // ============= 유틸리티 함수 =============

        formatDate(dateString) {
            return new Date(dateString).toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        getFileExtension(fileName) {
            return fileName.split('.').pop().toLowerCase();
        },

        getFileIcon(fileName) {
            const extension = this.getFileExtension(fileName);
            const iconMap = {
                'pdf': '📄',
                'doc': '📝', 'docx': '📝',
                'xls': '📊', 'xlsx': '📊',
                'ppt': '📽️', 'pptx': '📽️',
                'jpg': '🖼️', 'jpeg': '🖼️', 'png': '🖼️', 'gif': '🖼️', 'webp': '🖼️',
                'zip': '🗜️', 'rar': '🗜️',
                'txt': '📃'
            };
            return iconMap[extension] || '📁';
        }
    }
}
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>